import React, { useState, useEffect } from 'react';

const ArticleForm = ({ article, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Mécanique',
    status: 'draft',
    image: []
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title || '',
        slug: article.slug || '',
        excerpt: article.excerpt || '',
        content: article.content || '',
        category: article.category || 'Mécanique',
        status: article.status || 'draft',
        image: article.image || []
      });
      
      // Charger les aperçus des images existantes
      if (article.image && article.image.length > 0) {
        const previews = article.image.map(img => ({
          url: `https://christophedepanne.infocolab.online/api/files/articles/${article.id}/${img}`,
          name: img,
          existing: true
        }));
        setImagePreview(previews);
      }
    }
  }, [article]);

  // Fonction pour générer un slug à partir du titre
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      // Remplacer les caractères accentués
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Remplacer les espaces et caractères spéciaux par des tirets
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Fonction pour compresser une image
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculer les nouvelles dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Dessiner l'image redimensionnée
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir en blob compressé
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Fonction pour gérer l'ajout d'images
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const newImageFiles = [];
    const newPreviews = [];
    
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        // Compresser l'image
        const compressedFile = await compressImage(file);
        const compressedFileWithName = new File([compressedFile], file.name, {
          type: 'image/jpeg'
        });
        
        newImageFiles.push(compressedFileWithName);
        
        // Créer l'aperçu
        const preview = {
          url: URL.createObjectURL(compressedFileWithName),
          name: file.name,
          existing: false
        };
        newPreviews.push(preview);
      }
    }
    
    setImageFiles(prev => [...prev, ...newImageFiles]);
    setImagePreview(prev => [...prev, ...newPreviews]);
  };

  // Fonction pour supprimer une image
  const removeImage = (index) => {
    setImagePreview(prev => {
      const newPreviews = [...prev];
      const removed = newPreviews.splice(index, 1)[0];
      
      // Libérer l'URL de l'objet si ce n'est pas une image existante
      if (!removed.existing) {
        URL.revokeObjectURL(removed.url);
        // Supprimer aussi du tableau des fichiers
        setImageFiles(prevFiles => {
          const newFiles = [...prevFiles];
          const fileIndex = prevFiles.findIndex(f => f.name === removed.name);
          if (fileIndex > -1) {
            newFiles.splice(fileIndex, 1);
          }
          return newFiles;
        });
      }
      
      return newPreviews;
    });
  };

  const categories = [
    'Mécanique',
    'Entretien',
    'Diagnostic',
    'Réparation',
    'Conseils',
    'Actualités'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si c'est le titre qui change, générer automatiquement le slug
    if (name === 'title') {
      const newSlug = generateSlug(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        slug: newSlug
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }
    
    if (!formData.slug.trim()) {
      newErrors.slug = 'Le slug est requis';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Le slug ne peut contenir que des lettres minuscules, chiffres et tirets';
    }
    
    if (!formData.excerpt.trim()) {
      newErrors.excerpt = 'L\'extrait est requis';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Le contenu est requis';
    }
    
    if (formData.content.length < 100) {
      newErrors.content = 'Le contenu doit faire au moins 100 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Créer un FormData pour inclure les fichiers
      const submitData = {
        ...formData,
        imageFiles: imageFiles,
        existingImages: imagePreview.filter(img => img.existing).map(img => img.name)
      };
      onSubmit(submitData);
    }
  };

  return (
    <div className="article-form-overlay">
      <div className="article-form-container">
        <div className="article-form-header">
          <h2>{article ? 'Modifier l\'article' : 'Nouvel article'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="article-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Titre de l'article *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Entrez le titre de l'article"
                className={errors.title ? 'error' : ''}
              />
              {errors.title && <span className="error-text">{errors.title}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="slug">Slug URL *</label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              placeholder="slug-de-larticle"
              className={errors.slug ? 'error' : ''}
            />
            <div className="slug-preview">
              URL: /blog/{formData.slug || 'slug-de-larticle'}
            </div>
            {errors.slug && <span className="error-text">{errors.slug}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="image">Images de l'article</label>
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleImageChange}
                accept="image/*"
                multiple
                className="file-input"
              />
              <div className="file-input-help">
                Formats acceptés: JPG, PNG, GIF. Les images seront automatiquement compressées.
              </div>
              
              {/* Aperçu des images */}
              {imagePreview.length > 0 && (
                <div className="image-preview-grid">
                  {imagePreview.map((preview, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={preview.url} alt={`Aperçu ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => removeImage(index)}
                        title="Supprimer cette image"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                      <div className="image-name">{preview.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="category">Catégorie</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">Extrait *</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Résumé court de l'article (150-200 caractères)"
              rows="3"
              maxLength="200"
              className={errors.excerpt ? 'error' : ''}
            />
            <div className="char-count">{formData.excerpt.length}/200</div>
            {errors.excerpt && <span className="error-text">{errors.excerpt}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="content">Contenu de l'article *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Rédigez le contenu complet de votre article ici..."
              rows="12"
              className={errors.content ? 'error' : ''}
            />
            <div className="char-count">{formData.content.length} caractères</div>
            {errors.content && <span className="error-text">{errors.content}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="status">Statut</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Enregistrement...' : (article ? 'Mettre à jour' : 'Créer l\'article')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .article-form-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2000;
            padding: 20px;
          }

          .article-form-container {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
            animation: modalAppear 0.3s ease-out;
          }

          @keyframes modalAppear {
            from {
              opacity: 0;
              transform: translateY(-50px) scale(0.9);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .article-form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
          }

          .article-form-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
          }

          .close-btn {
            background: none;
            border: none;
            cursor: pointer;
            color: #6b7280;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .article-form {
            padding: 0 24px 24px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }

          .form-group {
            margin-bottom: 20px;
            position: relative;
          }

          .form-group label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.9rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 12px 16px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            font-size: 1rem;
            transition: all 0.2s ease;
            box-sizing: border-box;
            font-family: inherit;
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-group input.error,
          .form-group textarea.error {
            border-color: #dc2626;
          }

          .form-group textarea {
            resize: vertical;
            min-height: 100px;
          }

          .char-count {
            position: absolute;
            bottom: -20px;
            right: 0;
            font-size: 0.8rem;
            color: #6b7280;
          }

          .slug-preview {
            margin-top: 6px;
            padding: 8px 12px;
            background: #f3f4f6;
            border-radius: 6px;
            font-size: 0.85rem;
            color: #374151;
            font-family: monospace;
            border-left: 3px solid #3b82f6;
          }

          .file-input {
            padding: 12px 16px;
            border: 2px dashed #d1d5db;
            border-radius: 8px;
            background: #f9fafb;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .file-input:hover {
            border-color: #3b82f6;
            background: #eff6ff;
          }

          .file-input-help {
            font-size: 0.8rem;
            color: #6b7280;
            margin-top: 4px;
          }

          .image-preview-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 12px;
            margin-top: 16px;
          }

          .image-preview-item {
            position: relative;
            border-radius: 8px;
            overflow: hidden;
            background: #f3f4f6;
            aspect-ratio: 1;
          }

          .image-preview-item img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .remove-image-btn {
            position: absolute;
            top: 4px;
            right: 4px;
            background: rgba(239, 68, 68, 0.9);
            color: white;
            border: none;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .remove-image-btn:hover {
            background: rgba(239, 68, 68, 1);
            transform: scale(1.1);
          }

          .image-name {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 4px 6px;
            font-size: 0.7rem;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          .error-text {
            color: #dc2626;
            font-size: 0.8rem;
            margin-top: 4px;
            display: block;
          }

          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 32px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
          }

          .btn-cancel {
            padding: 12px 24px;
            border: 1px solid #d1d5db;
            background: white;
            color: #374151;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .btn-cancel:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }

          .btn-submit {
            padding: 12px 24px;
            border: none;
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            color: white;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .btn-submit:hover:not(:disabled) {
            background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
            transform: translateY(-1px);
          }

          .btn-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          @media (max-width: 768px) {
            .article-form-container {
              margin: 10px;
              max-width: none;
            }

            .form-row {
              grid-template-columns: 1fr;
              gap: 0;
            }

            .article-form-header {
              padding: 20px 20px 0;
            }

            .article-form {
              padding: 0 20px 20px;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ArticleForm;
